// AIDEV-NOTE: View-specific service degradation indicators.
// Only render on views with outbound LLM calls (chat, reflections, SOAP notes,
// upload, session processing, meditation). NOT for page-wide layout use.
// Three variants for different form factors:
//   - Banner: full-width for page-level views
//   - Notice: compact inline for cards/modals
//   - Badge: minimal indicator for tight spaces

import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { useServiceHealth } from "~/hooks/use-service-health";
import { cn } from "~/lib/utils";

/**
 * Full-width banner for page-level LLM views (chat, reflections, meditations, upload).
 * Sits at the top of the content area within the specific view.
 */
export function ServiceDegradationBanner() {
    const { degraded } = useServiceHealth();

    if (!degraded) return null;

    return (
        <Alert
            className={cn(
                "flex items-center rounded-lg border",
                "border-amber-200 bg-amber-50 text-amber-900",
            )}
            data-testid="service-degradation-banner"
        >
            <AlertDescription className="flex w-full items-center gap-3 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                <div>
                    <span className="font-medium">
                        Some features may be slower than usual right now.
                    </span>{" "}
                    <span className="opacity-80">
                        One of our partner services is experiencing temporary
                        difficulties. Your sessions and data are safe — things
                        should return to normal shortly.
                    </span>
                </div>
            </AlertDescription>
        </Alert>
    );
}

/**
 * Compact notice for inline components (session processing card, modals).
 * Smaller footprint, single-line message.
 */
export function ServiceDegradationNotice() {
    const { degraded } = useServiceHealth();

    if (!degraded) return null;

    return (
        <div
            className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="service-degradation-notice"
        >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>
                Processing may be slower than usual — a partner service is
                experiencing temporary difficulties.
            </span>
        </div>
    );
}

/**
 * Minimal badge for tight spaces (button areas, headers).
 * Shows only an icon with a tooltip-style title.
 */
export function ServiceDegradationBadge() {
    const { degraded } = useServiceHealth();

    if (!degraded) return null;

    return (
        <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700"
            data-testid="service-degradation-badge"
            title="A partner service is experiencing temporary difficulties. Features may be slower than usual."
        >
            <AlertTriangle className="h-3 w-3 text-amber-500" />
            <span>Slower than usual</span>
        </span>
    );
}
